import { Fragment, useEffect, useId, useRef } from 'react'
import type { ReactNode } from 'react'
import type { Transaction } from '../../api/types'
import { useT } from '../../i18n'
import type { BusinessTime } from '../../time/businessTime'
import { IconButton, ServiceTypeTag, StatusBadge } from '../../ui'
import { useGatewayBeneficiaries } from '../../api/queries'
import {
  FIELD_BENEFICIARY, FIELD_BILL_NUMBER, FIELD_COLLECTOR_COMPANY_BID, FIELD_CUS_NMB, FIELD_GATEWAY, FIELD_SERVICE, FIELD_SERVICE_TYPE, FIELD_SVC_NMB,
  type ExposedFields,
} from './exposure'
import { statusLabel, statusTone } from './statusTone'
import screen from '../screen.module.css'
import styles from './transactions.module.css'

export interface DetailPanelProps {
  readonly transaction: Transaction | null
  readonly onClose: () => void
  readonly businessTime: BusinessTime
  /** FR-38: the grantable fields the page's policy exposes — the gateway and
   *  service rows exist exactly when listed here, as the table's columns do. */
  readonly exposed: ExposedFields
  /** The organisation's settlement cut-off ("HH:MM"), named beside the settlement day. */
  readonly settlementCutOff?: string
}

/** A JSON document indented by two spaces; anything else exactly as given.
 *  A run of 16+ digits is shown as stored too: JSON.parse would round such a
 *  number, and a reconciliation screen must not show a value Core never held. */
const prettyJson = (raw: string): string => {
  if (/\d{16,}/.test(raw)) return raw
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

/**
 * A side sheet, not a modal (plan D6). Reconciliation means comparing a row
 * against something else on screen — a dialog that blacks out the table
 * defeats the task it exists to support.
 *
 * That choice has a cost and it is paid here rather than ignored: because the
 * panel is not modal, it must NOT trap focus (the table behind it stays
 * usable) but it must still be dismissable by keyboard and must return focus
 * where it came from. `role="region"` with a label, Escape to close, focus
 * moved in on open and restored on close.
 */
export function DetailPanel({ transaction, onClose, businessTime, exposed, settlementCutOff }: DetailPanelProps) {
  const t = useT()
  // Who holds this row's slice, from the gateway's beneficiaries panel data.
  // Enabled only when the identity is exposed: the endpoint is internal-only.
  const wantHolder = exposed.has(FIELD_BENEFICIARY) && Boolean(transaction?.beneficiary_code)
  const beneficiaries = useGatewayBeneficiaries(wantHolder && transaction ? transaction.gateway_id : null)
  const holder =
    beneficiaries.data?.beneficiaries.find((b) => b.beneficiary_code === transaction?.beneficiary_code)?.biller_name || null
  const headingId = useId()
  const panelRef = useRef<HTMLElement>(null)
  const returnFocusTo = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!transaction) return
    returnFocusTo.current = document.activeElement as HTMLElement | null
    panelRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      returnFocusTo.current?.focus?.()
    }
  }, [transaction, onClose])

  if (!transaction) return null

  // Data, not elements: JSX built inside an array literal needs a key on every
  // entry, and forgetting one is a silent reconciliation bug. Each row carries
  // a thunk instead, so the elements are created where they are rendered.
  const rows: ReadonlyArray<{ readonly term: string; readonly render: () => ReactNode }> = [
    { term: t('tx.ptn'), render: () => <span className={screen.mono}>{transaction.ptn}</span> },
    { term: t('tx.date'), render: () => businessTime.dateTime(transaction.created_at) },
    ...(transaction.settlement_day
      ? [{
          term: t('tx.settlementDayColumn'),
          render: () => t('tx.settlementDayDetail', {
            day: businessTime.settlementDay(transaction.settlement_day ?? ''),
            cutOff: settlementCutOff ?? '',
          }),
        }]
      : []),
    {
      term: t('tx.status'),
      render: () => (
        <StatusBadge tone={statusTone(transaction.transaction_status)}>
          {statusLabel(t, transaction.transaction_status)}
        </StatusBadge>
      ),
    },
    { term: t('tx.amount'), render: () => businessTime.money(transaction.amount) },
    // FR-39: always present; "n/a" while Ops set no name.
    { term: t('tx.serviceLabel'), render: () => transaction.service_label || t('tx.notAvailable') },
    ...(exposed.has(FIELD_GATEWAY)
      ? [{ term: t('tx.gateway'), render: () => transaction.gateway_name ?? '—' }]
      : []),
    ...(exposed.has(FIELD_SERVICE) || exposed.has(FIELD_SERVICE_TYPE)
      ? [
          {
            term: t('tx.service'),
            render: () => (
              <>
                {transaction.service_name ?? ''}{' '}
                {transaction.service_type ? <ServiceTypeTag type={transaction.service_type} /> : null}
              </>
            ),
          },
        ]
      : []),
    { term: t('tx.payerReference'), render: () => <span className={screen.mono}>{transaction.payer_reference}</span> },
    { term: t('tx.providerTxid'), render: () => <span className={screen.mono}>{transaction.provider_txid}</span> },
    // FR-42: the identity, its name and who holds the slice — internal only,
    // which is also why the holder lookup runs only when the field is exposed.
    ...(exposed.has(FIELD_BENEFICIARY) && transaction.beneficiary_code
      ? [
          {
            term: t('tx.beneficiary'),
            render: () => (
              <>
                <span className={screen.mono}>{transaction.beneficiary_code}</span>
                {transaction.beneficiary_name ? ` — ${transaction.beneficiary_name}` : ''}
              </>
            ),
          },
          { term: t('tx.beneficiaryHolder'), render: () => holder ?? t('tx.beneficiary.unassigned') },
        ]
      : []),
    ...(exposed.has(FIELD_BILL_NUMBER)
      ? [{ term: t('tx.billNumber'), render: () => <span className={screen.mono}>{transaction.bill_number ?? ''}</span> }]
      : []),
    ...(exposed.has(FIELD_SVC_NMB)
      ? [{ term: t('tx.svcNmb'), render: () => <span className={screen.mono}>{transaction.svc_nmb ?? ''}</span> }]
      : []),
    ...(exposed.has(FIELD_CUS_NMB)
      ? [{ term: t('tx.cusNmb'), render: () => <span className={screen.mono}>{transaction.cus_nmb ?? ''}</span> }]
      : []),
    ...(exposed.has(FIELD_COLLECTOR_COMPANY_BID)
      ? [{ term: t('tx.collectorCompanyBid'), render: () => <span className={screen.mono}>{transaction.collector_company_bid ?? ''}</span> }]
      : []),
    /*
      The configurable tier (TRD 8002). Each row appears only when the field is
      actually present, because the API omits a field the requester's policy
      withholds rather than sending it empty.

      Absent rather than blank, deliberately: a labelled row with nothing beside
      it says "we hold no value for this", which is a different claim from "you
      may not see this" — and the first is the one that starts an argument with
      a biller reconciling against their own records.
    */
    ...(transaction.payee_note
      ? [{ term: t('tx.note'), render: () => transaction.payee_note ?? '' }]
      : []),
    ...(transaction.fee !== undefined
      ? [{ term: t('tx.fee'), render: () => businessTime.money(transaction.fee ?? 0) }]
      : []),
    ...(transaction.collected_total !== undefined
      ? [{ term: t('tx.collectedTotal'), render: () => businessTime.money(transaction.collected_total ?? 0) }]
      : []),
    ...(transaction.paid_at
      ? [{ term: t('tx.paidAt'), render: () => businessTime.dateTime(transaction.paid_at ?? '') }]
      : []),
    ...(transaction.status_updated_at
      ? [{ term: t('tx.statusUpdatedAt'), render: () => businessTime.dateTime(transaction.status_updated_at ?? '') }]
      : []),
    ...(transaction.source_status
      ? [{ term: t('tx.sourceStatus'), render: () => <span className={screen.mono}>{transaction.source_status}</span> }]
      : []),
    // AB-41: the customer data as Core stored it, pretty-printed when it is JSON.
    ...(transaction.cdata
      ? [{ term: t('tx.cdata'), render: () => <pre className={`${screen.mono} ${styles.cdata}`}>{prettyJson(transaction.cdata ?? '')}</pre> }]
      : []),
  ]

  return (
    <aside
      ref={panelRef}
      className={styles.panel}
      role="region"
      aria-labelledby={headingId}
      tabIndex={-1}
    >
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle} id={headingId}>
          {t('tx.detailTitle')}
        </h2>
        <IconButton label={t('common.close')} size="sm" onClick={onClose}>
          ✕
        </IconButton>
      </div>
      <div className={styles.panelBody}>
        {/* A description list, because that is what this is: term/value pairs.
            A table here would announce phantom rows and columns. */}
        {/* dt/dd are direct children so the two-column grid on <dl> lays them
            out. Wrapping each pair in a <div> is the usual instinct and it
            breaks the grid — and adds a node with no semantics. */}
        <dl className={styles.definitions}>
          {rows.map((row) => (
            <Fragment key={row.term}>
              <dt className={styles.definitionTerm}>{row.term}</dt>
              <dd className={styles.definitionValue}>{row.render()}</dd>
            </Fragment>
          ))}
        </dl>
      </div>
    </aside>
  )
}
